import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import * as chatSocket from "../services/chatSocket";
import * as messagesService from "../services/messages";

// Unread messages count for teachers and parents. Updated live from the chat
// socket (which this hook keeps open), on page changes, when a conversation is
// read, and every 30 s as a fallback (every 2 min while the socket is up).
export default function useUnreadMessages(refreshKey) {
  const { accessToken, effectiveRole, user } = useAuth();
  const [unread, setUnread] = useState(0);
  const enabled =
    Boolean(accessToken) && ["teacher", "parent"].includes(effectiveRole);
  const myId = user?.id;

  const refresh = useCallback(() => {
    if (!enabled || document.visibilityState === "hidden") return;
    messagesService
      .fetchUnreadCount({ accessToken })
      .then((payload) => setUnread(Number(payload?.unread) || 0))
      .catch(() => {});
  }, [accessToken, enabled]);

  // Page changes.
  useEffect(() => {
    if (enabled) refresh();
    else setUnread(0);
  }, [enabled, refresh, refreshKey]);

  // Live updates + fallback timer. Kept apart from refreshKey so the socket
  // is not reopened on every navigation.
  useEffect(() => {
    if (!enabled) return undefined;
    const release = chatSocket.connectChat(accessToken);
    const unsubscribe = chatSocket.subscribe((event) => {
      const fromOther =
        event?.type === "message.new" &&
        Number(event.message?.sender?.id) !== Number(myId);
      const readByMe =
        event?.type === "conversation.read" &&
        Number(event.user_id) === Number(myId);
      if (fromOther || readByMe) refresh();
    });

    let timer = null;
    const stopStatus = chatSocket.onStatus((live) => {
      clearInterval(timer);
      timer = setInterval(refresh, live ? 120000 : 30000);
      if (live) refresh();
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(messagesService.MESSAGES_READ_EVENT, refresh);
    return () => {
      clearInterval(timer);
      stopStatus();
      unsubscribe();
      release();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(messagesService.MESSAGES_READ_EVENT, refresh);
    };
  }, [enabled, accessToken, myId, refresh]);

  // "(2) CasaClass" in the browser tab while messages are waiting.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
  }, [unread]);

  return { unread, enabled };
}
